import { useMemo } from 'react';

export interface SerializedIntakeWriteQueue {
  enqueue<T>(intakeItemId: string, write: () => Promise<T>): Promise<T>;
  enqueueMany<T>(intakeItemIds: string[], write: () => Promise<T>): Promise<T>;
}

/**
 * Serialize writes to the same Intake Item while allowing unrelated items to
 * save concurrently. A rejected write remains rejected for its caller, but is
 * normalized before it becomes the next write's dependency so retries and
 * subsequent edits are never poisoned by an earlier failure.
 */
export function createSerializedIntakeWriteQueue(): SerializedIntakeWriteQueue {
  const tails = new Map<string, Promise<void>>();

  const reserve = <T>(intakeItemIds: string[], write: () => Promise<T>): Promise<T> => {
    const uniqueItemIds = [...new Set(intakeItemIds)];
    const previous = uniqueItemIds.map(
      intakeItemId => tails.get(intakeItemId) ?? Promise.resolve(),
    );
    const result = Promise.all(previous).then(write);
    const normalizedTail = result.then(
      () => undefined,
      () => undefined,
    );

    for (const intakeItemId of uniqueItemIds) {
      tails.set(intakeItemId, normalizedTail);
    }
    void normalizedTail.then(() => {
      for (const intakeItemId of uniqueItemIds) {
        if (tails.get(intakeItemId) === normalizedTail) {
          tails.delete(intakeItemId);
        }
      }
    });

    return result;
  };

  return {
    enqueue<T>(intakeItemId: string, write: () => Promise<T>): Promise<T> {
      return reserve([intakeItemId], write);
    },
    enqueueMany<T>(intakeItemIds: string[], write: () => Promise<T>): Promise<T> {
      return reserve(intakeItemIds, write);
    },
  };
}

export function useSerializedIntakeWrites(): SerializedIntakeWriteQueue {
  return useMemo(() => createSerializedIntakeWriteQueue(), []);
}
